import React, { FC } from 'react';
import { useSelector } from 'react-redux';
import {
  clientNameSelector,
  userIdSelector,
} from '../../../../../redux/auth/selectors';
import DeleteUserConfirmation from './DeleteUserConfirmation.component';
import UserRoleConfirmation from './UserRoleConfirmation.component';
import { roleTranslator } from './../ManageAccounts.component';
import {
  isUciluxClient,
  useUciluxReadOnlyConfigByUserId,
} from '../../../../../temporary/uciluxAuditorReadOnly/uciluxAuditorReadOnly.hack';
import UciluxUserConfigSelector from '../../../../../temporary/uciluxAuditorReadOnly/uciluxUserConfigSelector.hack';
import dayjs from 'dayjs';
import TokenService from '../../../../../services/token-service';
import FastAPIInstance from '../../../../../services/risksystem3-public-api';
import { useQuery } from '@tanstack/react-query';
import SearchBar from '../../../../../components/ui/inputs/SearchBar';

// [2024-08-27, Tom Walsh] - Temporary code to remove '_funds' accounts from the list after corresponding deactivation date
// Deactivation dates for accounts, deafault is end of october 2024
export const AccountDeactivationDates: { [index: string]: string } = {
  pure_funds: '2024-10-31',
  gemini_funds: '2024-10-31',
  epic_funds: '2024-10-31',
  gem_funds: '2024-10-31',
  snif_funds: '2024-10-31',
  trium_funds: '2024-10-31',
  quayside_funds: '2024-10-31',
  greshamhouse_funds: '2024-10-31',
  corum_butler_funds: '2024-10-31',
  bflexion_funds: '2024-10-31',
  wcapital_funds: '2024-10-31',
  coutts_uk_funds: '2024-12-10', // According to Jonathan, this account is just used internally for producing reports on the portal. We should make these reports available on internal frontend and then delete this account
  coutts_ie_funds: '2024-12-10', // According to Jonathan, this account is just used internally for producing reports on the portal. We should make these reports available on internal frontend and then delete this account
  adepa_funds: '2025-02-28',
  coutts_funds: '2025-02-28',
  coutts_product_team: '2025-02-28',
  zeus_funds: '2025-05-28',
  ebi_funds: '2025-06-10',
  hvb_funds: '2025-06-10',
  aletti_funds: '2025-10-31',
  buyandhold_funds: '2025-10-31',
};
// [2024-08-27, Tom Walsh] - End of temporary code

const UserList: FC = () => {
  const userName = useSelector(userIdSelector);
  const clientName = useSelector(clientNameSelector);
  const userDetails = TokenService.getUser();
  const clientId = userDetails?.user_client_id;
  // TEMPORARY HACK — src/temporary/uciluxAuditorReadOnly. Delete with that
  // folder when page access is a resource. The config column is UCI Lux only.
  const showUciluxConfigColumn = isUciluxClient(clientName);
  const {
    configByUserId: uciluxReadOnlyConfigByUserId,
    isReady: uciluxConfigsReady,
    isError: uciluxConfigsFailed,
  } = useUciluxReadOnlyConfigByUserId(clientId, clientName);
  const userListColumnCount = showUciluxConfigColumn ? 7 : 6;

  const [numberOfAccounts, setNumberOfAccounts] = React.useState<{
    current_number_of_users: number;
    maximum_allowed_users: number;
  } | null>(null);

  // Add state for deactivated users
  const [deactivatedUsers, setDeactivatedUsers] = React.useState<any[]>([]);

  const [searchQuery, setSearchQuery] = React.useState('');

  // Use React Query to fetch users
  const { data: users = [], refetch } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const response = await FastAPIInstance.get(
        `/v1/external_user_management/user_list/client_id/${clientId}`,
      );

      const ordered = response.data.reduce((a: any, c: any) => {
        if (c.user_name === userName) a.unshift(c);
        else a.push(c);
        return a;
      }, []);

      // Separate active and deactivated users
      const currentDate = dayjs();
      const active: any[] = [];
      const deactivated: any[] = [];

      ordered.forEach((user: any) => {
        if (
          user.user_name in AccountDeactivationDates &&
          dayjs(AccountDeactivationDates[user.user_name]) <= currentDate
        ) {
          deactivated.push(user);
        } else {
          active.push(user);
        }
      });

      // Store deactivated users in state
      setDeactivatedUsers(deactivated);

      // Return only active users
      return active;
    },
    enabled: !!clientId,
  });

  const filteredUsers = React.useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return users;
    return users.filter((user: { user_name: string; user_email: string }) => {
      const id = user.user_name?.toLowerCase() ?? '';
      const email = user.user_email?.toLowerCase() ?? '';
      return id.includes(q) || email.includes(q);
    });
  }, [users, searchQuery]);

  // Then use useEffect to fetch account limits when users data changes
  React.useEffect(() => {
    if (users.length > 0 || deactivatedUsers.length > 0) {
      // Get account limits after users are loaded
      FastAPIInstance.get(
        `/v1/external_user_management/max_create_account_status/client_id/${clientId}`,
      ).then((response) => {
        // Use the length of deactivated users list
        const numDeactivated = deactivatedUsers.length;

        setNumberOfAccounts({
          current_number_of_users:
            response.data.current_number_of_users - numDeactivated,
          maximum_allowed_users:
            response.data.maximum_allowed_users - numDeactivated,
        });
      });
    }
  }, [users, deactivatedUsers, clientId]);

  return (
    <div className="w-full">
      <div className="mb-2">
        <div className="inline-block rounded-md border border-gray-300 bg-white px-4 py-1">
          <span className="font-medium">Accounts:</span>{' '}
          {numberOfAccounts ? (
            <span>
              {numberOfAccounts.current_number_of_users} /{' '}
              {numberOfAccounts.maximum_allowed_users}
            </span>
          ) : (
            'Loading...'
          )}
        </div>
      </div>

      <div className="overflow-hidden rounded-md border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-200 p-4">
          <SearchBar
            id="user-list-search"
            searchHandler={setSearchQuery}
            debounceTime={300}
            placeholder="Search by user ID, email..."
            className="w-96"
          />
        </div>
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th
                scope="col"
                className="w-16 px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
              ></th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
              >
                User ID
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
              >
                Email
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
              >
                Expiry Date
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
              >
                Role
              </th>
              {/* TEMPORARY HACK — src/temporary/uciluxAuditorReadOnly. */}
              {showUciluxConfigColumn && (
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                >
                  Config
                </th>
              )}
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {users.length > 0 && filteredUsers.length === 0 ? (
              <tr>
                <td
                  colSpan={userListColumnCount}
                  className="px-6 py-8 text-center text-sm text-gray-500"
                >
                  No accounts match your search.
                </td>
              </tr>
            ) : (
              filteredUsers.map((user: any, index: number) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="whitespace-nowrap px-6 py-4">
                    <div className="flex items-center justify-center">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full border border-mainblue bg-gray-100 font-bold text-mainblue">
                        {user.user_name.charAt(0).toUpperCase()}
                      </div>
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-6 py-4">
                    <div className="flex items-center">
                      <div className="text-sm font-medium text-gray-900">
                        {user.user_name}
                      </div>
                      {user.user_name === userName && (
                        <span className="ml-2 inline-flex rounded-full bg-green-100 px-2 text-xs font-semibold leading-5 text-green-800">
                          It&apos;s You
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                    {user.user_email}
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                    {user.user_expiry_date ? (
                      user.user_expiry_date
                    ) : (
                      <span className="italic text-gray-400">Never</span>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                    {roleTranslator(user.user_type)}
                  </td>
                  {/* TEMPORARY HACK — src/temporary/uciluxAuditorReadOnly. */}
                  {showUciluxConfigColumn && clientId && (
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                      <UciluxUserConfigSelector
                        clientId={clientId}
                        userId={user.user_id}
                        userName={user.user_name}
                        currentConfigName={
                          uciluxReadOnlyConfigByUserId[user.user_id]
                        }
                        configsReady={uciluxConfigsReady}
                        configsFailed={uciluxConfigsFailed}
                        isCurrentUser={user.user_name === userName}
                      />
                    </td>
                  )}
                  <td className="whitespace-nowrap px-6 py-4 text-right text-sm font-medium">
                    <div className="flex space-x-2">
                      {user.user_name !== userName &&
                        user.user_type === 'external_individual' && (
                          <UserRoleConfirmation
                            user={user}
                            reloadUsers={refetch}
                          />
                        )}
                      {user.user_name !== userName && (
                        <DeleteUserConfirmation
                          user={user}
                          reloadUsers={refetch}
                        />
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UserList;
